import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ShoppingCart,
  ArrowRight,
  Package,
  BookOpen,
  Books as BooksIcon,
  Sparkle,
  ChalkboardTeacher,
  UsersThree,
  FilePdf,
  Presentation,
  Handshake,
  Cross,
  Compass,
  EnvelopeSimple,
} from "@phosphor-icons/react";
import { API_BASE } from "../lib/site";
import PublisherAttribution from "../components/PublisherAttribution";
import Seo from "../components/Seo";

const FLAGSHIP = {
  title: "From Diagnosis to Goodbye",
  subtitle:
    "Restoring the Patient\u2019s Voice Through Every Stage of the Journey",
  author: "Derrick Hall",
  authorCredentials: "Hospice Chaplain, Author & Grief Coach",
  amazonUrl:
    "https://www.amazon.com/Diagnosis-Goodbye-Restoring-Patients-Through/dp/0980212723/ref=tmm_pap_swatch_0",
  cover: "https://m.media-amazon.com/images/I/71pLPPxlr5L.jpg",
};

const StatusBadge = ({ status }) => {
  const map = {
    available: { text: "Available Now", cls: "bg-primary text-white" },
    coming: {
      text: "Coming Soon",
      cls: "bg-secondary text-white",
    },
    future: {
      text: "Future Release",
      cls: "bg-white text-secondary-dark border border-secondary/30",
    },
  };
  const s = map[status] || map.coming;
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1.5 rounded-full shadow-sm " +
        s.cls
      }
    >
      {status !== "available" && <Sparkle size={11} weight="fill" />}
      {s.text}
    </span>
  );
};

const SectionShell = ({ id, overline, title, intro, icon: Icon, tone = "primary", children }) => {
  const toneMap = {
    primary: "bg-primary/8 text-primary border-primary/10",
    warm: "bg-secondary/12 text-secondary-dark border-secondary/20",
  };
  return (
    <section
      id={id}
      data-testid={`books-section-${id}`}
      className="scroll-mt-28 py-16 sm:py-20 border-t border-ink-100"
    >
      <div className="container-x">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
            <span className="w-8 h-px bg-secondary" />
            {overline}
          </span>
          <div className="mt-5 flex items-start gap-4">
            {Icon && (
              <span className={"hidden sm:inline-flex items-center justify-center w-14 h-14 shrink-0 rounded-2xl border " + toneMap[tone]}>
                <Icon size={26} weight="duotone" />
              </span>
            )}
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-ink-900 leading-[1.1]">
                {title}
              </h2>
              {intro && (
                <p className="mt-4 text-ink-700 text-base sm:text-lg leading-relaxed">
                  {intro}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="mt-10 sm:mt-12">{children}</div>
      </div>
    </section>
  );
};

const PubCard = ({ pub, coverTone = "from-softblue/40 via-white to-secondary/10" }) => (
  <article
    data-testid={`pub-card-${pub.id}`}
    className="flex flex-col bg-white border border-ink-100 rounded-3xl overflow-hidden shadow-[0_18px_60px_-24px_rgba(15,23,42,0.10)] hover:shadow-[0_28px_80px_-24px_rgba(15,23,42,0.18)] transition-shadow duration-300"
  >
    <div className={"relative bg-gradient-to-br border-b border-ink-100 p-5 " + coverTone}>
      {pub.cover ? (
        <img
          src={pub.cover}
          alt={pub.alt || pub.title + " cover"}
          loading="lazy"
          className="w-full h-auto max-h-[380px] object-contain mx-auto drop-shadow-[0_16px_32px_rgba(15,23,42,0.18)] rounded-md"
        />
      ) : (
        <div className="aspect-[3/4] w-full max-w-[220px] mx-auto rounded-md bg-white/80 border border-ink-100 flex flex-col items-center justify-center text-center p-5">
          {pub.icon ? (
            <pub.icon size={38} weight="duotone" className="text-primary" />
          ) : (
            <BooksIcon size={38} weight="duotone" className="text-primary" />
          )}
          <p className="mt-4 font-heading text-base text-ink-800 leading-snug">
            {pub.title}
          </p>
          <p className="mt-3 text-[10px] uppercase tracking-[0.22em] text-ink-500 font-semibold">
            Cover Forthcoming
          </p>
        </div>
      )}
      <span className="absolute top-4 right-4">
        <StatusBadge status={pub.status} />
      </span>
    </div>
    <div className="flex-1 flex flex-col p-6">
      <h3 className="font-heading text-xl text-ink-900 font-medium leading-snug">
        {pub.title}
      </h3>
      {pub.subtitle && (
        <p className="mt-2 font-heading text-sm text-ink-700 italic leading-snug">
          {pub.subtitle}
        </p>
      )}
      {pub.author && (
        <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-primary font-semibold">
          By {pub.author}
          {pub.authorCredentials && (
            <span className="normal-case tracking-normal text-ink-600 font-medium">
              {" \u2014 "}{pub.authorCredentials}
            </span>
          )}
        </p>
      )}
      {pub.description && (
        <p className="mt-4 text-sm text-ink-700 leading-relaxed flex-1">
          {pub.description}
        </p>
      )}
      {pub.cta && (
        <div className="mt-6">
          {pub.cta.external ? (
            <a
              href={pub.cta.to}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`pub-cta-${pub.id}`}
              className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
            >
              <ShoppingCart size={14} weight="duotone" />
              {pub.cta.label}
            </a>
          ) : (
            <Link
              to={pub.cta.to}
              data-testid={`pub-cta-${pub.id}`}
              className="inline-flex items-center gap-2 text-primary font-semibold text-sm hover:gap-3 transition-all"
            >
              {pub.cta.label}
              <ArrowRight size={14} weight="bold" />
            </Link>
          )}
        </div>
      )}
    </div>
  </article>
);

const NewsletterInline = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/newsletter`,
        {
          email,
          source_page:
            typeof window !== "undefined" ? window.location.href : null,
        },
        { withCredentials: false }
      );
      toast.success("Subscribed. Please check your inbox for a welcome note.");
      setEmail("");
    } catch {
      toast.error("Sorry, we couldn't subscribe you right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <form
      onSubmit={submit}
      data-testid="books-newsletter-form"
      className="mt-8 max-w-xl flex flex-col sm:flex-row gap-3"
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        data-testid="books-newsletter-email"
        className="flex-1 rounded-full border border-ink-200 px-5 py-3 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
        aria-label="Email address for the ministry newsletter"
      />
      <button
        type="submit"
        disabled={submitting}
        data-testid="books-newsletter-submit"
        className="inline-flex items-center justify-center gap-2 bg-primary text-white rounded-full px-6 py-3 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 disabled:opacity-60"
      >
        <EnvelopeSimple size={18} weight="duotone" />
        {submitting ? "Subscribing..." : "Subscribe"}
      </button>
    </form>
  );
};

const Books = () => {
  return (
    <div data-testid="page-books">
      <Seo
        title="Books & Publications by Chaplain Derrick Hall"
        description="Explore books, workbooks, and devotional guides by Chaplain Derrick Hall — resources for grief, caregiving, hospice, and life's most difficult transitions."
      />
      {/* HERO */}
      <section
        data-testid="books-hero"
        className="relative pt-24 pb-16 sm:pt-32 sm:pb-20 overflow-hidden"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-gradient-to-br from-softblue/60 via-white to-secondary/5"
        />
        <div
          aria-hidden="true"
          className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-secondary/10 blur-3xl -z-10"
        />
        <div className="container-x max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
            <span className="w-8 h-px bg-secondary" />
            The Publishing Ministry
          </span>
          <h1
            data-testid="books-hero-title"
            className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] text-ink-900"
          >
            Books &amp; Publications
          </h1>
          <p
            data-testid="books-hero-tagline"
            className="mt-5 font-heading text-xl sm:text-2xl text-ink-700 italic leading-snug"
          >
            The Publishing Ministry of Begin Again Living Ministries
          </p>
          <div className="mt-8 space-y-4 text-ink-700 text-lg leading-relaxed">
            <p>
              Publishing compassionate books and educational materials for
              individuals, families, caregivers, churches, healthcare
              professionals, and organizations.
            </p>
            <p>
              Discover books, workbooks, facilitator guides, participant
              resources, educational publications, and professional training
              materials created to educate, encourage, and equip people with
              practical tools, compassionate guidance, and hope throughout
              life&rsquo;s most significant journeys.
            </p>
          </div>
        </div>
      </section>

      {/* FEATURED PUBLICATION */}
      <section
        id="featured"
        data-testid="books-section-featured"
        className="scroll-mt-28 py-16 sm:py-20 border-t border-ink-100"
      >
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              Featured Publication
            </span>
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-ink-900 leading-[1.1]">
              The flagship of the Educational Platform.
            </h2>
          </div>

          <div className="mt-12 grid lg:grid-cols-[340px,1fr] gap-10 lg:gap-14 items-start">
            <div className="max-w-[340px] w-full mx-auto lg:mx-0">
              <img
                src={FLAGSHIP.cover}
                alt={`${FLAGSHIP.title} — book cover`}
                data-testid="books-flagship-cover"
                loading="lazy"
                className="w-full h-auto aspect-[3/4] object-cover rounded-2xl border border-ink-100 shadow-[0_24px_70px_-24px_rgba(15,23,42,0.28)]"
              />
            </div>
            <div
              data-testid="books-flagship-card"
              className="bg-white border border-ink-100 rounded-3xl p-8 sm:p-10 shadow-[0_18px_60px_-24px_rgba(15,23,42,0.08)]"
            >
              <span className="inline-flex items-center">
                <StatusBadge status="available" />
              </span>
              <h3
                data-testid="books-flagship-title"
                className="mt-5 font-heading text-3xl sm:text-4xl text-ink-900 font-medium leading-[1.1]"
              >
                {FLAGSHIP.title}
              </h3>
              <p className="mt-3 font-heading text-lg sm:text-xl text-ink-700 italic leading-snug">
                {FLAGSHIP.subtitle}
              </p>
              <p className="mt-4 text-xs uppercase tracking-[0.18em] text-primary font-semibold">
                By {FLAGSHIP.author}
                <span className="normal-case tracking-normal text-ink-600 font-medium">
                  {" \u2014 "}{FLAGSHIP.authorCredentials}
                </span>
              </p>
              <p className="mt-6 text-ink-700 leading-relaxed">
                The foundation of the <em>{FLAGSHIP.title}</em> Educational
                Platform, helping individuals, families, caregivers,
                churches, hospice organizations, and healthcare
                professionals navigate serious illness, caregiving, hospice
                care, grief, and meaningful conversations with greater
                compassion, confidence, and hope.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={FLAGSHIP.amazonUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="books-flagship-buy"
                  aria-label={`Buy ${FLAGSHIP.title} on Amazon (opens in a new tab)`}
                  className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-6 py-3 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
                >
                  <ShoppingCart size={18} weight="duotone" />
                  Buy on Amazon
                </a>
                <Link
                  to="/from-diagnosis-to-goodbye"
                  data-testid="books-flagship-learn"
                  className="inline-flex items-center gap-2 border-2 border-primary text-primary rounded-full px-6 py-3 font-semibold hover:bg-primary/5 transition-colors"
                >
                  <Compass size={18} weight="duotone" />
                  Learn More
                </Link>
                <Link
                  to="/bulk-orders"
                  data-testid="books-flagship-bulk"
                  className="inline-flex items-center gap-2 border-2 border-ink-200 text-ink-800 rounded-full px-6 py-3 font-semibold hover:border-primary hover:text-primary transition-colors"
                >
                  <Package size={18} weight="duotone" />
                  Bulk Orders
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GUIDED COMPANION SERIES */}
      <SectionShell
        id="companions"
        overline="Guided Companion Series"
        title="Expanding the Journey"
        intro={`Designed to accompany the ${FLAGSHIP.title} Educational Platform, these guided companions encourage personal reflection, meaningful conversation, and practical application.`}
        icon={BookOpen}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {[
            {
              id: "hospice-workbook",
              title: `${FLAGSHIP.title} Hospice Workbook`,
              subtitle:
                "A Guided Companion for Preparation, Presence, and Family Conversation",
              author: FLAGSHIP.author,
              authorCredentials: FLAGSHIP.authorCredentials,
              status: "available",
              cover:
                "/workbooks/From_Diagnosis_to_Goodbye_Hospice_Workbook_Front_Cover.png",
              alt: "Cover of the From Diagnosis to Goodbye Hospice Workbook by Derrick Hall",
              description:
                "For individuals and families navigating hospice — space for honest conversation, thoughtful preparation, meaningful connection, and compassionate presence throughout the journey.",
              cta: {
                external: true,
                to: "https://www.amazon.com/dp/0980212731",
                label: "Purchase on Amazon",
              },
            },
            {
              id: "bereavement-workbook",
              title: `${FLAGSHIP.title} Bereavement Workbook`,
              subtitle:
                "A Guided Companion for Reflection, Healing, and Life After Loss",
              author: FLAGSHIP.author,
              authorCredentials: FLAGSHIP.authorCredentials,
              status: "available",
              cover:
                "/workbooks/From_Diagnosis_to_Goodbye_Bereavement_Workbook_Front_Cover.png",
              alt: "Cover of the From Diagnosis to Goodbye Bereavement Workbook by Derrick Hall",
              description:
                "For individuals and families adjusting to life after a death — structured opportunities for reflection, remembrance, emotional expression, and gentle movement toward healing.",
              cta: {
                external: true,
                to: "https://www.amazon.com/dp/098021274X",
                label: "Purchase on Amazon",
              },
            },
            {
              id: "scripture-companion",
              title: `${FLAGSHIP.title} Scripture Companion`,
              subtitle:
                "Biblical Comfort and Guidance for Every Step of the Journey",
              author: FLAGSHIP.author,
              authorCredentials: FLAGSHIP.authorCredentials,
              status: "coming",
              cover:
                "/workbooks/From_Diagnosis_to_Goodbye_Scripture_Companion_Front_Cover.png",
              alt: "Cover of the From Diagnosis to Goodbye Scripture Companion by Derrick Hall",
              description:
                "A scripture-anchored companion offering biblical reflections, prayers, and passages of comfort for individuals and families walking through diagnosis, caregiving, hospice, and grief.",
            },
          ].map((p) => (
            <PubCard key={p.id} pub={p} />
          ))}
        </div>
      </SectionShell>

      {/* PROFESSIONAL EDUCATION COLLECTION */}
      <SectionShell
        id="professional"
        overline="Professional Education Collection"
        title="Resources for Organizations & Facilitators"
        intro="Professional educational resources developed for hospice organizations, churches, healthcare professionals, educators, chaplains, and community leaders."
        icon={ChalkboardTeacher}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {[
            {
              id: "facilitator-guides",
              title: "Facilitator Guides",
              subtitle:
                "Combined, Church Small-Group, Senior Living, and Hospice Staff Training",
              status: "coming",
              cover:
                "/facilitator-guides/FDTG_Facilitator_Combined_Hospice_Bereavement.png",
              alt: "Cover of the From Diagnosis to Goodbye Combined Facilitator Guide by Derrick Hall",
              description:
                "Structured session plans, teaching outlines, discussion questions, and practical activities for pastors, chaplains, hospice professionals, and small-group facilitators.",
              cta: { to: "/from-diagnosis-to-goodbye#facilitator-guides", label: "Explore the Educational Platform" },
            },
            {
              id: "participant-guide",
              title: `${FLAGSHIP.title} Participant Guide`,
              subtitle:
                "A Personal Companion for Group Study, Workshops, and Guided Learning",
              author: FLAGSHIP.author,
              authorCredentials: FLAGSHIP.authorCredentials,
              status: "coming",
              cover:
                "/workbooks/From_Diagnosis_to_Goodbye_Participant_Guide_Front_Cover.png",
              alt: "Cover of the From Diagnosis to Goodbye Participant Guide by Derrick Hall",
              description:
                "Practical companion materials for individuals participating in From Diagnosis to Goodbye workshops, seminars, and facilitated group sessions.",
            },
            {
              id: "licensing-handbook",
              title: "Licensing Handbook",
              subtitle: "Organizational Use, Facilitator Authorization & Program Standards",
              status: "coming",
              cover:
                "/workbooks/From_Diagnosis_to_Goodbye_Licensing_Handbook_Front_Cover.png",
              alt: "Cover of the From Diagnosis to Goodbye Licensing Handbook by Derrick Hall",
              description:
                "Information and guidance for organizations interested in authorized use, implementation, and licensing of the From Diagnosis to Goodbye program and materials.",
              cta: { to: "/from-diagnosis-to-goodbye#licensing", label: "Explore the Educational Platform" },
            },
            {
              id: "training-materials",
              title: "Training Materials",
              subtitle: "For Hospice, Church, and Community Programs",
              status: "coming",
              cover:
                "/workbooks/From_Diagnosis_to_Goodbye_Training_Materials_Front_Cover.png",
              alt: "Cover of the From Diagnosis to Goodbye Training Materials by Derrick Hall",
              description:
                "Structured educational resources designed for hospices, churches, senior living communities, healthcare teams, caregivers, and other organizations.",
              cta: { to: "/from-diagnosis-to-goodbye", label: "Explore the Educational Platform" },
            },
            {
              id: "presentation-resources",
              title: "Presentation Resources",
              subtitle: "Slides, Handouts, and Facilitator Companion Media",
              status: "coming",
              cover:
                "/workbooks/From_Diagnosis_to_Goodbye_Presentation_Resources_Front_Cover.png",
              alt: "Cover of the From Diagnosis to Goodbye Presentation Resources",
              description:
                "Presentation materials designed to support workshops, seminars, staff education, professional training, and facilitated programs.",
              cta: { to: "/from-diagnosis-to-goodbye", label: "Explore the Educational Platform" },
            },
          ].map((p) => (
            <PubCard key={p.id} pub={p} />
          ))}
        </div>
      </SectionShell>

      {/* CHAPLAIN DERRICK HALL PROFESSIONAL LIBRARY */}
      <SectionShell
        id="chaplain-library"
        overline="Chaplain Derrick Hall Professional Library"
        title="For Chaplains, Caregivers, and Compassionate Leaders"
        intro="A growing collection of professional publications written to strengthen chaplaincy, spiritual care, caregiver support, leadership, and compassionate ministry."
        icon={BooksIcon}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {[
            {
              id: "hospice-shift",
              title: "The Hospice Shift",
              subtitle:
                "Understanding Hospice While Specializing in Professional Spiritual Care",
              amazon:
                "https://www.amazon.com/Hospice-Shift-Understanding-Specializing-Professional-ebook/dp/B0G2LGL1ZL/",
              cover:
                "https://images-na.ssl-images-amazon.com/images/P/B0G2LGL1ZL.01._SCLZZZZZZZ_.jpg",
            },
            {
              id: "neurospiritual-chaplain",
              title: "The Neurospiritual Chaplain",
              subtitle:
                "How Faith, Presence, and Connection Promote Healing",
              amazon:
                "https://www.amazon.com/Neurospiritual-Chaplain-Presence-Connection-Promote-ebook/dp/B0H5VNLY7M/",
              cover:
                "https://images-na.ssl-images-amazon.com/images/P/B0H5VNLY7M.01._SCLZZZZZZZ_.jpg",
            },
            {
              id: "emotional-first-aid",
              title: "Chaplain: Emotional First Aid",
              subtitle:
                "A Practical Guide for Crisis Intervention and Spiritual Care",
              amazon:
                "https://www.amazon.com/Chaplain-Emotional-Intervention-Competencies-Developing-ebook/dp/B0H6F8QVLR/",
              cover:
                "https://images-na.ssl-images-amazon.com/images/P/B0H6F8QVLR.01._SCLZZZZZZZ_.jpg",
            },
          ].map((b) => (
            <article
              key={b.id}
              data-testid={`prolib-card-${b.id}`}
              className="flex flex-col bg-white border border-ink-100 rounded-3xl p-6 shadow-[0_18px_60px_-24px_rgba(15,23,42,0.10)] hover:-translate-y-1 hover:shadow-[0_28px_80px_-24px_rgba(15,23,42,0.18)] transition-all duration-300"
            >
              <div className="relative bg-gradient-to-br from-softblue/40 via-white to-secondary/10 border border-ink-100 rounded-2xl p-4">
                <img
                  src={b.cover}
                  alt={`${b.title} — book cover`}
                  loading="lazy"
                  data-testid={`prolib-cover-${b.id}`}
                  className="w-full h-auto max-h-[300px] object-contain mx-auto drop-shadow-[0_14px_28px_rgba(15,23,42,0.18)] rounded-md"
                />
                <span className="absolute top-3 right-3">
                  <StatusBadge status="available" />
                </span>
              </div>
              <div className="mt-5 flex-1 flex flex-col">
                <h3 className="font-heading text-xl text-ink-900 font-medium leading-snug">
                  {b.title}
                </h3>
                <p className="mt-2 font-heading text-sm text-ink-700 italic leading-snug flex-1">
                  {b.subtitle}
                </p>
                <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-primary font-semibold">
                  By {FLAGSHIP.author}
                </p>
                <a
                  href={b.amazon}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`prolib-buy-${b.id}`}
                  aria-label={`Buy ${b.title} on Amazon (opens in a new tab)`}
                  className="mt-5 inline-flex items-center justify-center gap-2 bg-primary text-white rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/25"
                >
                  <ShoppingCart size={16} weight="duotone" />
                  Buy on Amazon
                </a>
              </div>
            </article>
          ))}
        </div>
      </SectionShell>

      {/* VICTORY SERIES — visually distinct */}
      <section
        id="victory"
        data-testid="books-section-victory"
        className="scroll-mt-28 py-16 sm:py-20 border-t border-ink-100 bg-gradient-to-b from-secondary/8 via-white to-white"
      >
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-secondary-dark font-semibold">
              <Cross size={14} weight="duotone" className="text-secondary" />
              Faith &amp; Christian Living Collection
            </span>
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-ink-900 leading-[1.1]">
              Victory Series
            </h2>
            <p className="mt-5 text-ink-700 text-base sm:text-lg leading-relaxed">
              Biblically grounded resources designed to encourage spiritual
              growth, perseverance, faithful discipleship, and hope through
              every season of life.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-7">
            {[
              {
                id: "victory-babylon",
                title: "Victory in Babylon",
                subtitle: "Thriving in a Hostile Culture",
                amazon:
                  "https://www.amazon.com/Victory-Babylon-Thriving-Hostile-Culture-ebook/dp/B0H4YRSPK8/",
                cover:
                  "https://images-na.ssl-images-amazon.com/images/P/B0H4YRSPK8.01._SCLZZZZZZZ_.jpg",
              },
              {
                id: "victory-egypt",
                title: "Victory in Egypt",
                subtitle: "Thriving Through Seasons of Waiting and Preparation",
                amazon:
                  "https://www.amazon.com/Victory-Egypt-Thriving-Through-Seasons-ebook/dp/B0H5828FBR/",
                cover:
                  "https://images-na.ssl-images-amazon.com/images/P/B0H5828FBR.01._SCLZZZZZZZ_.jpg",
              },
              {
                id: "victory-gethsemane",
                title: "Victory in Gethsemane",
                subtitle: "Learning Surrender Before Victory",
                amazon:
                  "https://www.amazon.com/Victory-Gethsemane-Learning-Surrender-Before-ebook/dp/B0H5FTLKXK/",
                cover:
                  "https://images-na.ssl-images-amazon.com/images/P/B0H5FTLKXK.01._SCLZZZZZZZ_.jpg",
              },
              {
                id: "victory-failure",
                title: "Victory After Failure",
                subtitle: "Finding Restoration and Second Chances",
                amazon:
                  "https://www.amazon.com/Victory-After-Failure-Restoration-Chances-ebook/dp/B0H5BH4T3J/",
                cover:
                  "https://images-na.ssl-images-amazon.com/images/P/B0H5BH4T3J.01._SCLZZZZZZZ_.jpg",
              },
            ].map((v) => (
              <article
                key={v.id}
                data-testid={`victory-card-${v.id}`}
                className="flex flex-col bg-white border border-secondary/20 rounded-3xl p-6 shadow-[0_18px_60px_-24px_rgba(198,155,78,0.22)] hover:-translate-y-1 hover:shadow-[0_28px_80px_-24px_rgba(198,155,78,0.35)] transition-all duration-300"
              >
                <div className="relative bg-gradient-to-br from-secondary/15 via-white to-primary/8 border border-secondary/15 rounded-2xl p-4">
                  <img
                    src={v.cover}
                    alt={`${v.title} — book cover`}
                    loading="lazy"
                    data-testid={`victory-cover-${v.id}`}
                    className="w-full h-auto max-h-[260px] object-contain mx-auto drop-shadow-[0_14px_28px_rgba(15,23,42,0.18)] rounded-md"
                  />
                  <span className="absolute top-3 right-3">
                    <StatusBadge status="available" />
                  </span>
                </div>
                <div className="mt-5 flex-1 flex flex-col">
                  <h3 className="font-heading text-lg text-ink-900 font-medium leading-snug">
                    {v.title}
                  </h3>
                  <p className="mt-2 font-heading text-sm text-ink-700 italic leading-snug flex-1">
                    {v.subtitle}
                  </p>
                  <p className="mt-3 text-[10px] uppercase tracking-[0.22em] text-primary font-semibold">
                    By {FLAGSHIP.author}
                  </p>
                  <a
                    href={v.amazon}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`victory-buy-${v.id}`}
                    aria-label={`Buy ${v.title} on Amazon (opens in a new tab)`}
                    className="mt-5 inline-flex items-center justify-center gap-2 bg-primary text-white rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/25"
                  >
                    <ShoppingCart size={16} weight="duotone" />
                    Buy on Amazon
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FUTURE PUBLICATIONS NOTE */}
      <section className="py-16 border-t border-ink-100">
        <div className="container-x max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
            <span className="w-8 h-px bg-secondary" />
            Future Publications
          </span>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-medium text-ink-900 leading-[1.15]">
            The catalog continues to grow.
          </h2>
          <p className="mt-6 text-ink-700 text-lg leading-relaxed">
            Begin Again Living Ministries continues developing books,
            workbooks, facilitator resources, participant materials,
            educational programs, and ministry publications. Future titles
            will be added as they are released.
          </p>
        </div>
      </section>

      {/* STAY CONNECTED */}
      <PublisherAttribution testIdPrefix="books-attribution" />
      <section
        data-testid="books-newsletter"
        className="py-20 sm:py-24 border-t border-ink-100 bg-softblue/40"
      >
        <div className="container-x max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
            <span className="w-8 h-px bg-secondary" />
            Stay Connected
          </span>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-ink-900 leading-[1.15]">
            Stay Connected
          </h2>
          <p className="mt-6 text-ink-700 text-lg leading-relaxed">
            Be the first to hear about:
          </p>
          <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-ink-700">
            {[
              "New book releases",
              "Workbook launches",
              "Educational resources",
              "Workshops & seminars",
              "Facilitator materials",
              "Ministry updates",
            ].map((i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-secondary" aria-hidden="true" />
                {i}
              </li>
            ))}
          </ul>
          <NewsletterInline />
        </div>
      </section>
    </div>
  );
};

export default Books;
